import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Share2, Trophy, Timer } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(s / 60);
  const rem = s - mins * 60;
  return `${String(mins).padStart(2, "0")}:${rem.toFixed(1).padStart(4, "0")}`;
}

/**
 * Dashboard tile — shows the user's per-city personal bests and lets
 * them share any single run to friends via the Web Share sheet.
 * When shared, the message pre-pacs their time + their invite link so
 * friends can immediately try to beat them.
 */
export default function BestTimesCard() {
  const [times, setTimes] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, inv] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/best_times/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/invites/mine`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setTimes(t.data);
        setInviteCode(inv.data?.code);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const shareTime = useCallback(async (row) => {
    const link = inviteCode
      ? `${window.location.origin}/invite/${inviteCode}`
      : window.location.origin;
    const text =
      `I ran ${row.city_name} in ${formatTime(row.time_seconds)} on Nitro Auto Heist. ` +
      `Beat me → ${link}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `My ${row.city_name} best — Nitro Auto Heist`,
          text,
          url: link,
        });
      } catch (_e) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Copied — paste it to a friend");
      } catch (_e) {
        toast.error("Couldn't copy");
      }
    }
  }, [inviteCode]);

  if (!times) return null;

  const rows = times.best_times || [];
  const total = times.total_cities || 10;

  return (
    <div className="surface-solid p-6" data-testid="dashboard-best-times-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="label-kicker flex items-center gap-1.5">
            <Timer className="w-3 h-3" /> Your Bests
          </div>
          <div className="font-heading text-2xl mt-1">Personal Records</div>
        </div>
        <div className="font-mono text-xs text-white/40">
          {rows.length}/{total} CITIES
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-white/50 border border-dashed border-white/10 p-5 text-center">
          <Timer className="w-6 h-6 mx-auto mb-2 text-white/30" />
          Finish a race in any city to lock in your first record.
        </div>
      ) : (
        <div className="divide-y divide-white/5 border border-white/10">
          {rows.map((row, i) => (
            <div
              key={row.city_id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors"
              data-testid={`best-time-row-${row.city_id}`}
            >
              <div className="w-7 text-center font-mono text-xs text-white/30">
                {i === 0 ? (
                  <Trophy className="w-4 h-4 text-[#FFCC00] mx-auto" />
                ) : (
                  <span>#{i + 1}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base leading-tight">{row.city_name}</div>
                {row.set_at && (
                  <div className="text-[10px] font-mono tracking-wider text-white/30 uppercase mt-0.5">
                    {new Date(row.set_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="font-mono text-lg text-[#FFCC00] tabular-nums" data-testid={`best-time-value-${row.city_id}`}>
                {formatTime(row.time_seconds)}
              </div>
              <button
                onClick={() => shareTime(row)}
                data-testid={`best-time-share-${row.city_id}`}
                className="p-2 border border-white/10 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
                aria-label={`Share ${row.city_name} time`}
              >
                <Share2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
